import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "../../_common/hooks/useTranslation";
import { useDispatch } from "react-redux";
import { SAGA_ACTIONS } from "../../_config";

interface Currency {
  id: number;
  currency: string;
  currency_symbol: string;
  currency_name: string;
}

interface CurrencySelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  currenciesData: Currency[];
  selectedCurrency: string;
  onCurrencySelect: (currency: string, currencySymbol: string) => void;
}

const CurrencySelectionModal: React.FC<CurrencySelectionModalProps> = ({
  isOpen,
  onClose,
  currenciesData,
  selectedCurrency,
  onCurrencySelect,
}) => {
  const { t } = useTranslation("job-create");
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  // Debounced API call for currency search
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm("");
      return;
    }

    const timeoutId = setTimeout(() => {
      if (searchTerm.trim() !== "") {
        setIsSearching(true);
        dispatch({
          type: SAGA_ACTIONS.COMMON.CURRENCIES,
          payload: { q: searchTerm.trim(), limit: 150 },
        });
        // Reset searching state after API call
        setTimeout(() => setIsSearching(false), 500);
      } else {
        // Fetch all currencies when search is cleared
        dispatch({
          type: SAGA_ACTIONS.COMMON.CURRENCIES,
          payload: { limit: 150 },
        });
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchTerm, isOpen, dispatch]);

  if (!isOpen) {
    return null;
  }

  const handleCurrencySelect = (currency: Currency) => {
    onCurrencySelect(currency.currency, currency.currency_symbol);
    onClose();
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  return (
    <div className="currency-modal-overlay" onClick={onClose}>
      <div className="currency-modal" onClick={(e) => e.stopPropagation()}>
        <div className="currency-modal-header">
          <h3 className="currency-head">{t("jobCreate.currencyModal.title")}</h3>
          <button className="close-modal" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="currency-search-box">
          <input
            type="text"
            className="currency-search-input"
            placeholder={t("jobCreate.currencyModal.searchPlaceholder")}
            value={searchTerm}
            onChange={handleSearchChange}
            autoFocus
          />
        </div>

        <div className="currency-list">
          {isSearching ? (
            <div className="currency-loading">{t("jobCreate.currencyModal.searching") || "Searching..."}</div>
          ) : currenciesData && currenciesData.length > 0 ? (
            currenciesData.map((currency: Currency) => (
              <div
                key={currency.id}
                className={`currency-option${selectedCurrency === currency.currency ? " selected" : ""}`}
                onClick={() => handleCurrencySelect(currency)}
              >
                <div>
                  <div className="cur-name">{currency.currency_name}</div>
                  <div className="cur-code">{currency.currency}</div>
                </div>
                <div className="cur-symbol">{currency.currency_symbol}</div>
              </div>
            ))
          ) : (
            <div className="currency-no-results">{t("jobCreate.currencyModal.noResults") || "No currencies found"}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CurrencySelectionModal;
